import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { showModal } from 'modal.react.js';
import { Copy, Trash, XCircle } from 'phosphor-react';
import { clone, count, equals, isEmpty, isNil, reduceWhile } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { NumberParam, useQueryParam } from 'use-query-params';

import { PrimaryImageWarning } from 'components/Pages/Admin/shared/Modals/PrimaryImageWarning';
import { parseDate } from 'components/shared/BaseFormComponents/TimeInput/utils';
import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import Check2 from 'components/shared/Icon/icons/Check2';
import Email from 'components/shared/Icon/icons/Email';
import store from 'configureStore';
import { EDITING_PERMISSION_BLOCK } from 'constants/texts';
import { EBulkAction, EFlightStatus, EOrganizedBy, ETimelineType, ETripEvent, TTripEvent } from 'detailsv2/structure';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import {
  mergeTripEventsAction,
  createTripEventAction,
  setCollapsedDaysAction,
  setOrganizedByAction,
  updateTripEventAction,
  duplicateTripEventsAction,
  addMergeItinerariesDataAction,
} from 'store/itinerariesItem';
import {
  collapsedDaysSelector,
  startDateSelector,
  tripEventSelector,
  tripEventsSelector,
} from 'store/itinerariesItem/chronologicalDetails/selectors';
import { itinerariesItemDataSelector, itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { updateItinerariesItemThunk } from 'store/itinerariesItem/thunks';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import appsignal from 'utils/appsignal';
import { reverseFlightDateValues } from 'utils/common';
import { addDaysToDate, getCurrentDateString, getHoursAndMinutes12, getHoursAndMinutes24 } from 'utils/date';
import errorTracker from 'utils/errorTracker';
import { useItinerariesItemContext } from 'utils/itineraries/context';
import { isSample } from 'utils/itineraries/functions';
import { useIgnorePrimaryImageWarning } from 'utils/itineraries/functions/useIgnorePrimaryImageWarning';
import { useItinerariesItemStepPath } from 'utils/itineraries/functions/useItinerariesItemStepPath';
import { isDefined } from 'utils/ramda';

import s from '../../../GuestPortal/Flights/index.module.css';
import { IAction } from '../ActionsMenu/types';
import { SuccessResult } from '../MergeItineraryModal/index.types';
import { MergeItineraryModal } from '../MergeItineraryModal/MergeItineraryModal';
import { EDetailsMode } from '../types';
import { ACTIVE_TRIP_EVENT_ID_QUERY_PARAM, addEventActions, mergeEventsAction, timelineWarningsMap } from './constants';
import { deleteTripEvents } from './Event/ModalContents/DeleteDialog/functions';
import { editTripEvent } from './Event/ModalContents/Edit/functions';
import { CalendarEvent } from './EventCalendar/Calendar/types';
import { useTimelineContext } from './TimelineContext';
import { ITimelineContext } from './TimelineContext/types';
import {
  ETimelineWarningType,
  IDay,
  IInitialTimelineState,
  ITimelineWarning,
  ITripEventCreateAction,
  TimeFormat,
} from './types';
import { getTimelineDays, isTripEventValid } from './utils';

export const useTimelineContextValue = (): ITimelineContext => {
  const dispatch = useDispatch();
  const history = useHistory();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const tripEvents: TTripEvent[] = useSelector(tripEventsSelector);
  const { itemId, itemType, nextStep } = useItinerariesItemContext();
  const getStepPath = useItinerariesItemStepPath(itemType);
  const timelineDays = useMemo(() => getTimelineDays(tripEvents), [tripEvents]);
  const [checkIsWarningIgnored, setToIgnoreWarning] = useIgnorePrimaryImageWarning();

  const [selectedEvents, setSelectedEvents] = useState<CalendarEvent[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [initialTimelineState, setInitialTimelineState] = useState<IInitialTimelineState>({
    tripEvents,
    organizedBy: itinerary.scheduleType,
    collapsedDays: itinerary.collapsedDays,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shownTimelineWarnings, setShownTimelineWarnings] = useState<ETimelineWarningType[]>([]);

  const isTripEventsChanged = useMemo(() => {
    return !equals<IInitialTimelineState>(initialTimelineState, {
      tripEvents: itinerary.tripEvents,
      organizedBy: itinerary.scheduleType,
      collapsedDays: itinerary.collapsedDays,
    });
  }, [initialTimelineState, itinerary.tripEvents, itinerary.scheduleType, itinerary.collapsedDays]);

  const warn = useCallback(async () => {
    if (!isTripEventsChanged) {
      return Promise.resolve();
    }

    const warnings = getTimelineWarnings(timelineDays, shownTimelineWarnings);

    if (isEmpty(warnings)) {
      return Promise.resolve();
    }

    return new Promise<void>((promiseRes, promiseRej) => {
      showModal(area, (res) => {
        const resolveAlert = (cancel: boolean) => {
          setShownTimelineWarnings([...shownTimelineWarnings, warnings[0].type]);
          res();
          cancel ? promiseRes() : promiseRej('cancel');
        };

        return (
          <AlertModal
            headerText="Heads Up!"
            confirmButtonText="Confirm"
            footerClassName={s.alertModalButtons}
            res={() => resolveAlert(false)}
            onAccept={() => resolveAlert(true)}
            bodyText={warnings[0].message}
          />
        );
      });
    });
  }, [timelineDays, isTripEventsChanged, shownTimelineWarnings]);

  const save = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const preparedTripEvents = getPreparedTripEventsBeforeSaving(itinerary);

      validateTripEvents(preparedTripEvents, itinerary.scheduleType);
      const proposal = (await dispatch(
        updateItinerariesItemThunk(itemId, {
          tripEvents: preparedTripEvents,
          collapsedDays: itinerary.collapsedDays,
          scheduleType: itinerary.scheduleType,
          detailsMode: itinerary.detailsMode,
          currentStep: 'details',
          mergeItineraries: itinerary?.mergeItineraries,
        }),
      )) as any;

      setInitialTimelineState({
        tripEvents: proposal.tripEvents,
        organizedBy: proposal.scheduleType,
        collapsedDays: proposal.collapsedDays,
      });

      const isIgnoreForProposal = checkIsWarningIgnored(proposal.id);
      if (!isIgnoreForProposal && proposal.showHeadsUpModal) {
        const res: { isIgnoreForProposal: boolean } | undefined = await showModal(area, (res) => (
          // @ts-expect-error
          <PrimaryImageWarning onClose={res} onSave={res} />
        ));
        if (res?.isIgnoreForProposal) setToIgnoreWarning(proposal.id);
      }

      return proposal;
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, itemId, itinerary]);

  const handleSave = useCallback(
    async (evt: React.MouseEvent<HTMLElement>) => {
      // ignore hitting "Enter" button from keyboard, bc button is in focus after first click with mouse
      if (evt.detail === 0) return;

      try {
        await warn();
        return await save();
      } catch (err) {
        if (err === 'cancel') return;

        errorCatcher(err);
      }
    },
    [warn, save],
  );

  const handleNext = useCallback(
    async (evt: React.MouseEvent<HTMLElement>) => {
      // ignore hitting "Enter" button from keyboard, bc button is in focus after first click with mouse
      if (evt.detail === 0) return;

      try {
        let response;
        if (isTripEventsChanged) {
          await warn();
          response = await save();
        }

        history.push(getStepPath(nextStep, itinerary.id));
        return response || itinerary;
      } catch (err) {
        if (err === 'cancel') return;

        errorCatcher(err);
      }
    },
    [warn, save, nextStep, getStepPath, history, isTripEventsChanged, itinerary.id],
  );

  useEffect(() => {
    dispatch(setAppHasChangesAction(isTripEventsChanged));
  }, [isTripEventsChanged, dispatch]);

  return {
    timelineDays,
    isTripEventsChanged,
    isSubmitting,
    validationErrors: null,
    hasValidationErrors: false,
    handleSave,
    handleNext,
    selectedEvents,
    setSelectedEvents,
  };
};

export const useActiveBackgroundOnScroll = () => {
  const [activeBackground, setActiveBackground] = useState(false);
  const { isMobile } = useMediaBreakpoints();

  useEffect(() => {
    const headerID = document.getElementById('formSections');
    headerID!.style.marginTop = '0';

    const handleScroll = () => {
      const timelinePosition = document.getElementById('timeline')!.getBoundingClientRect().y;
      if (timelinePosition <= 100) {
        setActiveBackground(true);
      } else if (isMobile && timelinePosition <= 130) {
        setActiveBackground(true);
      } else {
        setActiveBackground(false);
      }
    };

    headerID!.addEventListener('scroll', handleScroll);

    return () => {
      headerID!.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  return { activeBackground };
};

export const useCollapseDays = () => {
  const isCollapsed = useSelector(collapsedDaysSelector);
  const dispatch = useDispatch();

  const setCollapsedDays = (value: boolean) => {
    dispatch(setCollapsedDaysAction(value));
  };

  return { setCollapsedDays, isCollapsed };
};

export function useChronologicalMode() {
  const dispatch = useDispatch();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const organizedBy = useSelector(itineraryScheduleTypeSelector);
  const tripEvents = useSelector(tripEventsSelector);
  const startDate: string | null = useSelector(startDateSelector);
  const { timelineDays, selectedEvents, setSelectedEvents } = useTimelineContext();
  const lastTimeLineDay = timelineDays && timelineDays[timelineDays.length - 1];
  const endDate = lastTimeLineDay ? lastTimeLineDay.date : null;
  const isOrganizedByDay = organizedBy === EOrganizedBy.Day;
  const isOrganizedByDate = organizedBy === EOrganizedBy.Date;

  const globalActions: IAction[] = useMemo(
    () => [
      {
        title: 'Switch to The Classic Builder (@mention)',
        icon: Email,
        tooltipText: 'You can’t switch the builder mode \n' + 'after events have been added.',
        onClick: async () => {
          // double saving request is temp solution for BE
          try {
            sessionStorage.setItem('hideConfirmChangesForCurrentTab', 'true');
            const tripEventsForSave = getPreparedTripEventsBeforeSaving(itinerary);

            if (tripEventsForSave.length > 0) {
              await dispatch(
                updateItinerariesItemThunk(itinerary.id, {
                  tripEvents: getPreparedTripEventsBeforeSaving(itinerary),
                }),
              );
            }

            await dispatch(
              updateItinerariesItemThunk(itinerary.id, {
                detailsMode: EDetailsMode.Original,
              }),
            );
          } catch (e) {
            sessionStorage.removeItem('hideConfirmChangesForCurrentTab');
          }
        },
        isDisabled: isDefined(tripEvents),
      },
    ],
    [dispatch, tripEvents, itinerary],
  );

  const bulkActions: IAction[][] = useMemo(() => {
    return [
      [
        {
          id: EBulkAction.Duplicate,
          title: 'Duplicate',
          onClick: () => {
            dispatch(duplicateTripEventsAction({ ids: selectedEvents.map((el) => el.id) }));
            setSelectedEvents([]);
          },
          icon: Copy,
        },
        {
          id: EBulkAction.Delete,
          title: 'Delete',
          onClick: async () => {
            // see useDeleteDialog#23 comment for why try/catch is used
            try {
              await deleteTripEvents(selectedEvents.map((el) => el.id));
            } catch (message: any) {
              if (message === 'deleted') {
                setSelectedEvents([]);
              }
            }
          },
          icon: Trash,
        },
      ],
      [
        {
          id: EBulkAction.ClearSelection,
          title: 'Clear Selection',
          onClick: () => setSelectedEvents([]),
          icon: XCircle,
        },
      ],
    ];
  }, [selectedEvents]);

  const organizedActions: IAction[] = useMemo(() => {
    return [
      {
        id: EOrganizedBy.Day,
        title: 'Show Dates',
        onClick: () => dispatch(setOrganizedByAction(EOrganizedBy.Day)),
        icon: isOrganizedByDay ? Check2 : '',
        isActive: isOrganizedByDay,
      },
      {
        id: EOrganizedBy.Date,
        title: 'Hide Dates',
        onClick: () => dispatch(setOrganizedByAction(EOrganizedBy.Date)),
        icon: isOrganizedByDate ? Check2 : '',
        isActive: isOrganizedByDate,
      },
    ];
  }, [dispatch, isOrganizedByDate, isOrganizedByDay]);

  const nextDayDateToCreate = useMemo(() => {
    if (isDefined(endDate)) return addDaysToDate(endDate, 1);
    if (isOrganizedByDate && !isDefined(endDate)) return getCurrentDateString();
    if (isOrganizedByDay && !isDefined(endDate)) return null;
  }, [isOrganizedByDate, isOrganizedByDay, endDate]);

  return {
    startDate: isOrganizedByDay ? null : startDate,
    nextDayToCreate: {
      dayNumber: !isNil(timelineDays) && !isEmpty(timelineDays) ? timelineDays[timelineDays.length - 1].day + 1 : 1,
      dayDate: nextDayDateToCreate,
    },
    chooseStartDateDisabled: !isDefined(startDate) || isOrganizedByDay,
    globalActions,
    organizedActions,
    bulkActions,
    endDate,
    organizedBy,
  };
}

export function useAddEventMenuActions({
  dayNumber = 1,
  dayDate = null,
}: {
  dayNumber: number;
  dayDate: string | null | undefined;
}) {
  const dispatch = useDispatch();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const scheduleType = useSelector(itineraryScheduleTypeSelector);
  const isOrganizedByDate = scheduleType === EOrganizedBy.Date;
  const { libType } = useParams<{ libType?: string }>();
  const isSampleItinerary = isSample(libType);

  const createTripEvent = React.useCallback(
    async (action: ITripEventCreateAction) => {
      const tripEvent = {
        type: action.type,
        daysRange: { start: dayNumber, end: null },
        datesRange: { start: dayDate, end: null },
        data: {},
      } as TTripEvent;

      const result = await editTripEvent(tripEvent);

      if (!result) return;

      result.forEach((el) => dispatch(createTripEventAction(el)));
    },
    [dayNumber, dayDate],
  );
  const mergeItinerary = React.useCallback(
    async (result: SuccessResult) => {
      const tripEvents = result.data?.tripEvents;
      if (tripEvents && tripEvents.length > 0) {
        await dispatch(
          mergeTripEventsAction({
            tripEvents,
            referenceDate: dayDate,
            referenceDay: dayNumber,
            targetOrganizedBy: scheduleType,
            sourceOrganizedBy: result.data!.scheduleType,
          }),
        );
        await dispatch(addMergeItinerariesDataAction({ mergeItineraryId: result.data?.id, referenceDay: dayNumber }));
      } else {
        throw new Error('No trip events found');
      }
    },
    [scheduleType, isOrganizedByDate, dayDate, dayNumber],
  );

  const handleMergeItinerary = React.useCallback(async () => {
    showModal(
      area,
      <MergeItineraryModal isSample={isSampleItinerary} itineraryId={itinerary.id} onFormSuccess={mergeItinerary} />,
    );
  }, [isSampleItinerary, itinerary.id, mergeItinerary]);

  const actions = React.useMemo(() => {
    return [
      // create action
      ...addEventActions.map((action) => ({
        ...action,
        onClick: () => createTripEvent(action),
      })),
      // merge action
      {
        ...mergeEventsAction,
        onClick: handleMergeItinerary,
      },
    ];
  }, [createTripEvent, handleMergeItinerary]);

  return actions;
}

export const removeLeadingZeros = (timeString: string | null) => {
  if (!timeString) return null;
  return timeString.replace(/^0(?:0:0?)?/, '');
};

export const transformTime = (timeString: string | null, timeFormat: TimeFormat = '12h') => {
  if (timeFormat === '24h') {
    if (!timeString) return null;

    return getHoursAndMinutes24(parseDate(timeString), ':');
  }

  return removeLeadingZeros(timeString);
};

export const transformAndRemoveLeadingZeros = (time: string | null) => {
  if (!time) return null;
  const updatedTime = getHoursAndMinutes12(new Date(time));
  if (updatedTime) return removeLeadingZeros(updatedTime);
};

function errorCatcher(err: any) {
  console.dir(err);
  if (typeof err === 'string') {
    return showErrorSnackbar("Maybe server 500 error, open browser's console to check");
  }

  if (err?.errors?.title?.includes('You don’t have editing permission anymore. Please contact your account admin.')) {
    showErrorSnackbar(EDITING_PERMISSION_BLOCK);
  } else {
    showErrorSnackbar(JSON.stringify(err));
  }
}

function getPreparedTripEventsBeforeSaving(itinerary: any): TTripEvent[] {
  const droppedTripEvents = itinerary.droppedTripEvents;
  const initialTripEvents = itinerary.tripEvents.map((t: any) => {
    if (t.type === ETripEvent.Flight) {
      const flightCopy = clone(t);

      if (flightCopy.data.fromFsService && flightCopy.data.flightStatus === EFlightStatus.Common) {
        flightCopy.data.cancelled = null;
        flightCopy.data.newArrivalTime = [];
        flightCopy.data.newDepartureTime = [];
      }

      delete flightCopy.data.flightStatus;

      if (flightCopy.data?.isIDL) {
        return reverseFlightDateValues(flightCopy);
      }
      return flightCopy;
    }
    return t;
  });

  return initialTripEvents.concat(
    (droppedTripEvents || []).map((id: number) => ({
      id,
      _destroy: true,
    })),
  );
}

function getTimelineWarnings(tripDays: IDay[], shownWarnings: ETimelineWarningType[]) {
  const warnings: ITimelineWarning[] = [];

  if (!shownWarnings.includes(ETimelineWarningType.WRONG_FLIGHT_INFO)) {
    const isFlightWarningEmpty = (warning: ITimelineWarning | null) => isNil(warning);
    const detectWrongFlight = (acc: ITimelineWarning | null, elem: IDay) => {
      for (const tripEvent of elem.tripEvents) {
        if (tripEvent.type === ETripEvent.Flight && tripEvent.data.fromFsService) {
          const organizedBy: EOrganizedBy = itineraryScheduleTypeSelector(store.getState());

          if (
            organizedBy === EOrganizedBy.Day ||
            (tripEvent.timelineType !== ETimelineType.End && tripEvent.data.flightDate !== tripEvent.date)
          ) {
            return timelineWarningsMap[ETimelineWarningType.WRONG_FLIGHT_INFO];
          }
        }
      }

      return null;
    };
    if (reduceWhile(isFlightWarningEmpty, detectWrongFlight, null, tripDays)) {
      warnings.push(timelineWarningsMap[ETimelineWarningType.WRONG_FLIGHT_INFO]);
    }
  }

  if (!shownWarnings.includes(ETimelineWarningType.EMPTY_DAYS)) {
    const isEmptyDay = (tripDay: IDay) => isEmpty(tripDay.tripEvents);
    const emptyDaysCount = count(isEmptyDay, tripDays);

    if (emptyDaysCount > 0) {
      warnings.push(timelineWarningsMap[ETimelineWarningType.EMPTY_DAYS]);
    }
  }

  return warnings;
}

export function useActiveTripEventModal() {
  const dispatch = useDispatch();
  const [activeTripEventId, setActiveTripEventId] = useQueryParam(ACTIVE_TRIP_EVENT_ID_QUERY_PARAM, NumberParam);
  const [wasShown, setWasShown] = useState(false);
  // @ts-ignore
  const tripEvent: TTripEvent | undefined = useSelector(tripEventSelector(activeTripEventId));
  const tripEvents = useSelector(tripEventsSelector);

  useEffect(() => {
    const run = async () => {
      if (tripEvent) {
        const result = await editTripEvent(tripEvent);
        setWasShown(true);
        setActiveTripEventId(undefined);

        if (!result) {
          return;
        }

        result.forEach((el) => dispatch(updateTripEventAction(el)));
      }
    };

    if (tripEvents.length > 0 && !wasShown) {
      run();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripEvent, tripEvents, wasShown]); // do not uncomment line above, only need to run once
}

function validateTripEvents(tripEvents: TTripEvent[], organizedBy: EOrganizedBy) {
  const errors: TTripEvent[] = [];

  tripEvents.forEach((t) => {
    if (!isTripEventValid(t, organizedBy)) errors.push(t);
  });

  if (errors.length === 0) return;

  // do not remove this log, it's used by appsignal
  console.log(errors);

  const span = appsignal.createSpan();
  span.setTags({ tag: 'value' }).setError(new Error('Trip Events validation failed'));
  span.setNamespace('trip_event_validation_errors');
  span.setAction('trip_event_validation');

  errorTracker.sendError(span as unknown as Error);
}
